import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { apiClient } from '@/lib/apiClient'

interface ConnectorSummary {
  connectorKey: string
  displayName: string
  verticalCode: string | null
  isInstalled: boolean
}

interface TestResult {
  ok: boolean
  message?: string
}

export function ConnectorMarketplacePage() {
  const { t } = useTranslation()
  const [connectors, setConnectors] = useState<ConnectorSummary[]>([])
  const [loadError, setLoadError] = useState(false)
  const [testingKey, setTestingKey] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<Record<string, TestResult>>({})

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<ConnectorSummary[]>('/connectors/marketplace')
      setConnectors(res.data)
    } catch {
      setLoadError(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const handleTest = async (key: string) => {
    setTestingKey(key)
    try {
      const res = await apiClient.post<TestResult>(`/connectors/${key}/test`, {})
      setTestResult(prev => ({ ...prev, [key]: res.data }))
    } catch {
      setTestResult(prev => ({
        ...prev,
        [key]: { ok: false, message: t('connectors.error_test') },
      }))
    } finally {
      setTestingKey(null)
    }
  }

  if (loadError) {
    return <p className="text-red-600 p-6">{t('connectors.error_load')}</p>
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('connectors.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('connectors.subtitle')}</p>
      </div>

      {connectors.length === 0 ? (
        <p className="text-muted-foreground">{t('connectors.empty')}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {connectors.map(c => (
            <div
              key={c.connectorKey}
              className="border rounded-lg p-4 space-y-3 bg-card shadow-sm"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{c.displayName}</h3>
                {c.isInstalled && (
                  <span className="text-xs bg-green-900/40 text-green-200 px-2 py-0.5 rounded-full">
                    {t('connectors.installed')}
                  </span>
                )}
              </div>
              {c.verticalCode && (
                <p className="text-xs text-muted-foreground">
                  {t(`vertical.verticals.${c.verticalCode}` as Parameters<typeof t>[0], {
                    defaultValue: c.verticalCode,
                  })}
                </p>
              )}
              {testResult[c.connectorKey] && (
                <p
                  className={`text-xs ${testResult[c.connectorKey].ok ? 'text-green-600' : 'text-red-600'}`}
                >
                  {testResult[c.connectorKey].ok
                    ? t('connectors.test_ok')
                    : t('connectors.test_failed', {
                        message: testResult[c.connectorKey].message,
                      })}
                </p>
              )}
              <div className="flex gap-2">
                {!c.isInstalled && (
                  <button className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">
                    {t('connectors.install')}
                  </button>
                )}
                <button
                  className="text-xs border px-3 py-1 rounded hover:bg-card disabled:opacity-50"
                  disabled={testingKey === c.connectorKey}
                  onClick={() => void handleTest(c.connectorKey)}
                >
                  {testingKey === c.connectorKey
                    ? t('connectors.testing')
                    : t('connectors.test')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
